/**
 * One list item (#337): its checkbox (top-level items of a checklist, and
 * sub-checkboxes), progress over its sub-checkboxes, the editable text, and
 * controls - jump to the line it was filed from, its task, and a menu.
 * Whether a top-level checkbox shows is decided in CSS from the list's kind,
 * so switching kind needs no re-render here.
 */
import { useRef } from 'react';
import { NodeViewWrapper, NodeViewContent, type NodeViewProps } from '@tiptap/react';
import { Locate, MoreHorizontal, SquareCheckBig } from 'lucide-react';
import { itemProgress, type NamedListItemOptions } from './NamedListNodes';
import { useOptionalEntityPreview } from '../../../contexts/EntityPreviewContext';
import { NamedListMenu } from './NamedListMenu';
import { useNamedListMenu } from './useNamedListMenu';

const LOCATED_MS = 1500;

export function NamedListItemView({ node, editor, getPos, extension }: NodeViewProps) {
  const { itemId, checked, checkbox, ref, taskId } = node.attrs as {
    itemId: string; checked: boolean; checkbox: boolean; ref: string | null; taskId: string | null;
  };
  const opts = extension.options as NamedListItemOptions;
  const preview = useOptionalEntityPreview();
  const menu = useNamedListMenu(editor);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const pos = typeof getPos === 'function' ? getPos() : undefined;
  const top = typeof pos === 'number' && editor.state.doc.resolve(pos).parent.type.name === 'namedList';
  const progress = itemProgress(node);

  const locate = () => {
    let at = -1;
    editor.state.doc.descendants((n, p, parent) => {
      if (at < 0 && n.attrs.lineId === ref && parent?.type.name !== 'namedListItem') at = p;
      return at < 0;
    });
    if (at < 0) return;
    const dom = editor.view.nodeDOM(at) as HTMLElement | null;
    if (!dom) return;
    dom.scrollIntoView({ block: 'center', behavior: 'smooth' });
    dom.classList.add('line-located');
    setTimeout(() => dom.classList.remove('line-located'), LOCATED_MS);
  };

  const remove = () => {
    menu.close();
    const p = typeof getPos === 'function' ? getPos() : undefined;
    if (typeof p === 'number') editor.view.dispatch(editor.state.tr.delete(p, p + node.nodeSize));
    editor.view.focus();
  };

  return (
    <NodeViewWrapper className="named-list-item" data-top={top ? 'true' : 'false'} data-checkbox={checkbox ? 'true' : 'false'} data-checked={checked ? 'true' : 'false'}>
      <button
        type="button"
        contentEditable={false}
        role="checkbox"
        aria-checked={checked}
        aria-label={checked ? 'Mark not done' : 'Mark done'}
        className="nli-check"
        onClick={() => editor.commands.toggleListItem(itemId)}
      />
      <NodeViewContent className="nli-body" />
      <span className="nli-controls" contentEditable={false}>
        {progress && <span className="nli-progress">{progress.done}/{progress.total}</span>}
        {taskId && (
          <button type="button" className="nli-btn" title="Open task" aria-label="Open task" onClick={() => preview?.openPreview('task', taskId)}>
            <SquareCheckBig size={13} />
          </button>
        )}
        {ref && (
          <button type="button" className="nli-btn" title="Show where this was written" aria-label="Show where this was written" onClick={locate}>
            <Locate size={13} />
          </button>
        )}
        <button ref={triggerRef} type="button" className="nli-btn" aria-label="Item menu" title="Item menu" aria-haspopup="menu" aria-expanded={menu.open} onClick={menu.toggle}>
          <MoreHorizontal size={13} />
        </button>
        {/* #358: portaled with a backdrop, so a tap outside cannot tick another item's checkbox. */}
        {menu.open && (
          <NamedListMenu anchor={triggerRef} label="Item menu" onDismiss={menu.dismiss}>
            {opts.onTurnItemIntoTask && !taskId && (
              <button type="button" role="menuitem" onClick={() => { menu.close(); opts.onTurnItemIntoTask!(itemId); }}>Turn into task</button>
            )}
            <button type="button" role="menuitem" onClick={remove}>Remove from list</button>
          </NamedListMenu>
        )}
      </span>
    </NodeViewWrapper>
  );
}
